/**
 * Your size, chosen for you.
 *
 * The shop already knows the customer's measurements — they typed them into
 * My sizes. This reads the size options actually on the page, works out what
 * scale they are in, and picks the one nearest the body.
 *
 * The scale is read from the option values rather than from the option's name,
 * because the name is "Size" on everything from a suit to a suitcase. The
 * values are what differ, and they differ in ways that matter:
 *
 *   38" Jacket 32" Trousers   a chest measurement in inches
 *   32W 32L                   a waist, and a leg
 *   UK 9                      a shoe — but only on a shoe
 *   UK 12                     a woman's dress size on a coat
 *   Medium                    a letter, and which letter depends on who it is for
 *   1-2 Years                 a child's age, which we do not hold
 *   3-Set                     a suitcase, which has no body at all
 *
 * "UK 9" and "UK 12" are the reason this is not a lookup table: identical
 * shape, one is a foot and the other a bust, and only the product's type tells
 * them apart. Everything it cannot read confidently it leaves alone — no
 * recommendation is much cheaper than a wrong one, which comes back as a
 * return and a lost customer.
 */
(function () {
  'use strict';

  /* Types where a size is a body measurement. A suitcase's "Medium" is not. */
  var WEARABLE = {
    'Suits': 1, 'Pant Suits': 1, 'Shirts & Tops': 1, 'Vests': 1,
    'Coats & Jackets': 1, 'Shoes': 1, 'Dresses': 1, 'Skirts': 1, 'Trousers': 1,
  };

  /* Chest in inches, per letter. Two charts, because a women's Medium and a
     men's Medium are four inches apart — the profile says which to use. */
  var ALPHA = {
    mens: { 'xs': 34, 's': 36, 'm': 39, 'l': 42, 'xl': 45, '2xl': 48, '3xl': 51, '4xl': 54, '5xl': 57 },
    womens: { 'xs': 32, 's': 34, 'm': 36, 'l': 38, 'xl': 40, '2xl': 42, '3xl': 44, '4xl': 46, '5xl': 48 },
  };

  function alphaKey(value) {
    var v = String(value).toLowerCase().replace(/[\s.-]/g, '');
    if (v === 'xsmall' || v === 'extrasmall' || v === 'xs') return 'xs';
    if (v === 'small' || v === 's') return 's';
    if (v === 'medium' || v === 'med' || v === 'm') return 'm';
    if (v === 'large' || v === 'l') return 'l';
    if (v === 'xlarge' || v === 'extralarge' || v === 'xl') return 'xl';
    var many = v.match(/^([2-5])x(l|large)?$/) || v.match(/^(xx|xxx|xxxx)l$/);
    if (many) {
      if (/^\d$/.test(many[1])) return many[1] + 'xl';
      return String(many[1].length + 1) + 'xl';
    }
    return null;
  }

  /**
   * What are these option values measuring, and where does each one sit on
   * that scale? Returns null when it cannot tell — which is a valid answer.
   */
  function readScale(values, productType, audience) {
    var wearable = !!WEARABLE[productType];
    var points = [];
    var i;

    /* A suit: the jacket number is the chest, in inches. */
    var suits = 0;
    for (i = 0; i < values.length; i++) {
      var m = String(values[i]).match(/^\s*(\d{2})\s*("|”|''|in)?\s*jacket/i);
      if (m) { points.push({ value: values[i], at: Number(m[1]) }); suits++; }
    }
    if (suits && suits === values.length) return { measure: 'chestIn', unit: 'in', points: points };

    /* Trousers: 32W 32L. The waist decides; the leg is carried so the closest
       leg can break a tie between two of the same waist. */
    points = [];
    var trousers = 0;
    for (i = 0; i < values.length; i++) {
      var t = String(values[i]).match(/^\s*(\d{2})\s*w\s*(\d{2})?\s*l?\s*$/i);
      if (t) { points.push({ value: values[i], at: Number(t[1]), leg: t[2] ? Number(t[2]) : null }); trousers++; }
    }
    if (trousers && trousers === values.length) return { measure: 'waistIn', unit: 'in', points: points };

    /* UK n. A foot on a shoe, a dress size on anything else worn. */
    points = [];
    var uk = 0;
    for (i = 0; i < values.length; i++) {
      var u = String(values[i]).match(/^\s*uk\s*(\d{1,2})(\.5)?\s*$/i);
      if (u) { points.push({ value: values[i], at: Number(u[1]) + (u[2] ? 0.5 : 0) }); uk++; }
    }
    if (uk && uk === values.length) {
      if (productType === 'Shoes') return { measure: 'shoeUk', unit: 'uk', points: points };
      if (wearable) {
        /* A UK dress size is the bust in inches, less 24: a UK 12 is a 36. */
        for (i = 0; i < points.length; i++) points[i].at = points[i].at + 24;
        return { measure: 'chestIn', unit: 'in', points: points, dress: true };
      }
      return null;
    }

    if (!wearable) return null; // a suitcase's "Medium" is not a chest

    /* Letters. */
    points = [];
    var letters = 0;
    var chart = ALPHA[audience === 'womens' ? 'womens' : 'mens'];
    for (i = 0; i < values.length; i++) {
      var key = alphaKey(values[i]);
      if (key && chart[key]) { points.push({ value: values[i], at: chart[key] }); letters++; }
    }
    if (letters && letters === values.length) {
      return { measure: 'chestIn', unit: 'in', points: points, alpha: true };
    }

    /* Bare chest numbers: 36, 38, 40 — used on some waistcoats and blazers. */
    points = [];
    var bare = 0;
    for (i = 0; i < values.length; i++) {
      var b = String(values[i]).match(/^\s*(\d{2})\s*$/);
      if (b && Number(b[1]) >= 30 && Number(b[1]) <= 60) {
        points.push({ value: values[i], at: Number(b[1]) }); bare++;
      }
    }
    if (bare && bare === values.length && bare > 1) {
      return { measure: 'chestIn', unit: 'in', points: points };
    }

    return null;
  }

  /**
   * The nearest size to the body.
   *
   * Ties go to the larger. A jacket an inch too big is taken in by any tailor;
   * an inch too small is a return, and this shop sells suits people are
   * wearing on a fixed date.
   */
  function nearest(scale, profile) {
    var target = profile[scale.measure];
    if (typeof target !== 'number' || !isFinite(target)) return null;

    var best = null;
    for (var i = 0; i < scale.points.length; i++) {
      var p = scale.points[i];
      var gap = Math.abs(p.at - target);
      if (!best || gap < best.gap - 0.0001 || (Math.abs(gap - best.gap) < 0.0001 && p.at > best.point.at)) {
        best = { point: p, gap: gap };
      }
    }
    if (!best) return null;

    /* A guess this far out is not a recommendation. Four inches of chest is
       two sizes; two UK shoe sizes is a different foot. */
    var limit = scale.unit === 'uk' ? 1.5 : 4;
    if (best.gap > limit) return null;

    /* Same waist, two legs: take the leg nearest theirs. */
    if (scale.measure === 'waistIn' && typeof profile.inseamIn === 'number') {
      var sameWaist = scale.points.filter(function (p) { return p.at === best.point.at && p.leg; });
      if (sameWaist.length > 1) {
        sameWaist.sort(function (a, b) {
          return Math.abs(a.leg - profile.inseamIn) - Math.abs(b.leg - profile.inseamIn);
        });
        best.point = sameWaist[0];
      }
    }
    return best.point;
  }

  /* What the recommendation is founded on, in the customer's own words. */
  function because(scale, profile) {
    if (scale.measure === 'chestIn') return 'your ' + profile.chestIn + '″ chest';
    if (scale.measure === 'waistIn') return 'your ' + profile.waistIn + '″ waist';
    if (scale.measure === 'shoeUk') return 'your UK ' + profile.shoeUk + ' foot';
    return 'your saved sizes';
  }

  /* ---- the page ------------------------------------------------------ */

  function radioGroups(root) {
    var groups = {};
    var radios = root.querySelectorAll('input[type="radio"][value]');
    for (var i = 0; i < radios.length; i++) {
      var name = radios[i].name;
      if (!name) continue;
      (groups[name] = groups[name] || []).push(radios[i]);
    }
    return groups;
  }

  function note(text, linkText, href) {
    var box = document.createElement('p');
    box.className = 'tcfr__fitnote';
    box.appendChild(document.createTextNode(text + ' '));
    if (linkText) {
      var a = document.createElement('a');
      a.href = href;
      a.textContent = linkText;
      box.appendChild(a);
    }
    return box;
  }

  function setup(root) {
    if (root.__tcFitReady) return;
    root.__tcFitReady = true;

    var picker = root.querySelector('variant-picker, .variant-picker');
    if (!picker) return;

    var productType = root.getAttribute('data-tcfr-type') || '';
    /* Who the garment is for, from its own title. A man's 38" chest maps
       arithmetically onto a women's UK 14 coat, and saying so would be
       confidently wrong: he is buying a present, and we know nothing about the
       person receiving it. Where the garment and the body disagree, say
       nothing. */
    var madeFor = root.getAttribute('data-tcfr-for') || 'mens';
    var sizesUrl = root.getAttribute('data-tcfr-sizes-url') || '/pages/my-sizes';

    /* Somebody who arrived on a specific variant has already chosen; moving
       the selection under them would be rude and, in a shared link, wrong. */
    var chosen = new URLSearchParams(location.search).has('variant');

    fetch('/apps/tru/fit', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (profile) {
        if (!profile || !profile.hasProfile) return invite();
        if (madeFor !== (profile.audience === 'womens' ? 'womens' : 'mens')) return;
        apply(profile);
      })
      .catch(function () { /* signed out, or the proxy is unwell: say nothing */ });

    function invite() {
      /* Only where we could have helped: a page with a size scale we read. */
      var groups = radioGroups(picker);
      for (var name in groups) {
        var values = groups[name].map(function (r) { return r.value; });
        if (readScale(values, productType, 'mens')) {
          picker.parentNode.insertBefore(
            note('Not sure of your size?', 'Save your measurements once and we will pick for you.', sizesUrl),
            picker,
          );
          return;
        }
      }
    }

    /* The variant map the section already publishes: every combination and
       whether it can be bought. Needed because a size radio is only disabled
       for the colour showing at that moment — a UK 9 can read "sold out" while
       sitting in stock in blue. */
    function variantMap() {
      /* The map is published after the section's markup, not inside it — the
         same reason tc-fitting-room.js looks outward too. Reading only inside
         found nothing, and a missing map meant every recommendation was made
         blind to stock: it preselected a sold-out black shoe in a size sitting
         in stock in blue. */
      var el = root.querySelector('[data-tcfr-variants]') ||
               (root.parentNode && root.parentNode.querySelector('[data-tcfr-variants]')) ||
               document.querySelector('[data-tcfr-variants]');
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (e) { return null; }
    }

    function apply(profile) {
      var groups = radioGroups(picker);
      for (var name in groups) {
        var radios = groups[name];
        var values = radios.map(function (r) { return r.value; });
        var scale = readScale(values, productType, profile.audience);
        if (!scale) continue;

        var pick = nearest(scale, profile);
        if (!pick) continue;

        var radio = null;
        for (var i = 0; i < radios.length; i++) {
          if (radios[i].value === pick.value) { radio = radios[i]; break; }
        }
        if (!radio) continue;

        var reason = ', from ' + because(scale, profile) + '.';
        var map = variantMap();
        var here = null;      // this size, in what is currently on screen
        var elsewhere = null; // this size, in some other colour

        if (map) {
          var others = [];
          for (var g in groups) {
            if (g === name) continue;
            for (var k = 0; k < groups[g].length; k++) {
              if (groups[g][k].checked) { others.push(groups[g][k].value); break; }
            }
          }
          for (var v = 0; v < map.length; v++) {
            var opts = map[v].options || [];
            if (opts.indexOf(pick.value) === -1) continue;
            var matchesRest = others.every(function (o) { return opts.indexOf(o) !== -1; });
            if (matchesRest && map[v].available) { here = map[v]; break; }
            if (!elsewhere && map[v].available) { elsewhere = map[v]; }
          }
        }

        if (map && !here && elsewhere) {
          /* It exists, just not in this colour. Say which, and offer it —
             rather than "sold out", which sends them away from a sale we can
             actually make. */
          var other = (elsewhere.options || []).filter(function (o) { return o !== pick.value; }).join(', ');
          var line = note('Your ' + pick.value + ' is in stock in ' + other + '.', null, null);
          var swap = document.createElement('button');
          swap.type = 'button';
          swap.className = 'tcfr__fitswap';
          swap.textContent = 'Show me that one';
          swap.addEventListener('click', function () {
            (elsewhere.options || []).forEach(function (value) {
              for (var g2 in groups) {
                for (var m = 0; m < groups[g2].length; m++) {
                  if (groups[g2][m].value === value && !groups[g2][m].checked) {
                    groups[g2][m].checked = true;
                    groups[g2][m].dispatchEvent(new Event('change', { bubbles: true }));
                  }
                }
              }
            });
            line.remove();
          });
          line.appendChild(swap);
          picker.parentNode.insertBefore(line, picker);
          return;
        }

        if (map && !here && !elsewhere) {
          picker.parentNode.insertBefore(
            note('Your usual ' + pick.value + ' is sold out in this one.', 'See your sizes', sizesUrl),
            picker,
          );
          return;
        }

        if (!chosen && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }

        picker.parentNode.insertBefore(
          note(
            (chosen ? 'We would suggest ' : 'Chosen for you: ') + pick.value + reason,
            'Change my sizes', sizesUrl,
          ),
          picker,
        );
        return;
      }
    }
  }


  /* ---- The complete look ---------------------------------------------
     Same rules, a different shape of control. On a product page each option
     is its own row; in an outfit the whole variant is one line of text —
     "White / Small / Regular Fit" — so the size has to be found among the
     parts. Every column is tried and the first that reads as a body scale
     wins; on that shirt it is the second, on a plain suit the only one. */
  function chooseInSelect(select, productType, profile) {
    var opts = [];
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].value) opts.push(select.options[i]);
    }
    if (!opts.length) return null;

    var rows = opts.map(function (o) {
      return {
        option: o,
        /* Drop the " — sold out" this section appends, or it becomes part of
           the value and nothing matches. */
        parts: o.textContent.replace(/\s*[—-]\s*sold out\s*$/i, '').split('/').map(function (x) {
          return x.trim();
        }),
      };
    });

    var columns = rows.reduce(function (n, r) { return Math.min(n, r.parts.length); }, 99);
    for (var c = 0; c < columns; c++) {
      var seen = {}, values = [];
      rows.forEach(function (r) {
        if (!seen[r.parts[c]]) { seen[r.parts[c]] = 1; values.push(r.parts[c]); }
      });
      var scale = readScale(values, productType, profile.audience);
      if (!scale) continue;
      var pick = nearest(scale, profile);
      if (!pick) continue;

      var column = c;
      var wanted = rows.filter(function (r) { return r.parts[column] === pick.value; });
      /* In stock beats exact colour: an outfit is bought as a whole. */
      var live = wanted.filter(function (r) { return !r.option.disabled; });
      var chosenRow = live[0] || null;
      if (!chosenRow) return { soldOut: true, value: pick.value, scale: scale };
      return { option: chosenRow.option, value: pick.value, scale: scale };
    }
    return null;
  }

  function dressLook(form, profile) {
    if (form.__tcFitLook) return;
    form.__tcFitLook = true;

    var note = form.querySelector('[data-tc-look-fitnote]');
    var picks = form.querySelectorAll('.tc-pick');
    var done = 0, missing = [];

    for (var i = 0; i < picks.length; i++) {
      var pick = picks[i];
      var select = pick.querySelector('[data-tc-piece]');
      if (!select || select.value) continue; // no sizes, or already chosen

      var madeFor = pick.getAttribute('data-tc-piece-for') || 'mens';
      if (madeFor !== (profile.audience === 'womens' ? 'womens' : 'mens')) continue;

      var result = chooseInSelect(select, pick.getAttribute('data-tc-piece-type') || '', profile);
      if (!result) continue;
      if (result.soldOut) { missing.push(result.value); continue; }

      select.value = result.option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      done++;
    }

    if (!note) return;
    if (done) {
      note.textContent = done === 1
        ? 'One size filled in from your saved measurements. Check it before you add the look.'
        : done + ' sizes filled in from your saved measurements. Check them before you add the look.';
      if (missing.length) {
        note.textContent += ' Your ' + missing.join(' and ') + ' is sold out here.';
      }
      note.hidden = false;
    } else if (missing.length) {
      note.textContent = 'Your usual ' + missing.join(' and ') + ' is sold out in this outfit.';
      note.hidden = false;
    }
  }

  function looks(scope) {
    var forms = (scope || document).querySelectorAll('[data-tc-look-form]');
    if (!forms.length) return;
    fetch('/apps/tru/fit', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (profile) {
        if (!profile || !profile.hasProfile) return;
        for (var i = 0; i < forms.length; i++) dressLook(forms[i], profile);
      })
      .catch(function () { /* signed out: the pickers stay as they were */ });
  }

  function boot(scope) {
    var roots = (scope || document).querySelectorAll('[data-tcfr]');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
    looks(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

  /* Exposed so the same rules can be checked without a browser. */
  window.tcFitSize = { readScale: readScale, nearest: nearest, chooseInSelect: chooseInSelect };
})();
